/* Plushington Allure — commerce: cart drawer, search, quick view, wishlist, bundles */
(function () {
  'use strict';

  var T = window.theme || {};
  var ROUTES = T.routes || {};
  var STRINGS = T.strings || {};
  var SETTINGS = T.settings || {};
  var WISHLIST_KEY = 'plushington_wishlist';

  document.addEventListener('DOMContentLoaded', function () {
    initCartDrawer();
    initSearchOverlay();
    initQuickView();
    initWishlist();
    initBundleWidget();
    initScrollReveal();
    initWelcomePopup();
    initAskAi();
  });

  /* ================= Shared helpers ================= */

  var CURRENT_CURRENCY = null;

  function formatMoney(cents, format) {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    if (CURRENT_CURRENCY && window.Intl && Intl.NumberFormat) {
      try {
        return new Intl.NumberFormat(undefined, { style: 'currency', currency: CURRENT_CURRENCY }).format((Number(cents) || 0) / 100);
      } catch (e) { /* fall through to legacy formatting */ }
    }
    format = format || (SETTINGS.moneyFormat || '${{amount}}');
    var value = '';
    var placeholder = /\{\{\s*(\w+)\s*\}\}/;
    var match = format.match(placeholder);

    function defaultTo(a, b) { return a == null ? b : a; }
    function formatWithDelimiters(number, precision, thousands, decimal) {
      precision = defaultTo(precision, 2);
      thousands = defaultTo(thousands, ',');
      decimal = defaultTo(decimal, '.');
      if (isNaN(number) || number == null) return 0;
      number = (number / 100.0).toFixed(precision);
      var parts = number.split('.');
      var dollars = parts[0].replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1' + thousands);
      var cents2 = parts[1] ? decimal + parts[1] : '';
      return dollars + cents2;
    }

    switch (match && match[1]) {
      case 'amount': value = formatWithDelimiters(cents, 2); break;
      case 'amount_no_decimals': value = formatWithDelimiters(cents, 0); break;
      case 'amount_with_comma_separator': value = formatWithDelimiters(cents, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': value = formatWithDelimiters(cents, 0, '.', ','); break;
      default: value = formatWithDelimiters(cents, 2);
    }
    return format.replace(placeholder, value);
  }

  function escapeHtml(str) {
    return String(str == null ? '' : str).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  var toastTimer = null;
  function showToast(text, variant, actionCallback, actionLabel) {
    var toast = document.getElementById('ToastNotification');
    if (!toast) {
      toast = document.createElement('div');
      toast.id = 'ToastNotification';
      toast.className = 'plush-toast';
      document.body.appendChild(toast);
    }
    var actionHtml = actionLabel ? '<button type="button" class="plush-toast__btn" data-plush-toast-action>' + escapeHtml(actionLabel) + ' &rarr;</button>' : '';
    toast.innerHTML = '<span class="plush-toast__icon">✦</span><span class="plush-toast__text">' + escapeHtml(text) + '</span>' + actionHtml;
    toast.className = 'plush-toast' + (variant ? ' plush-toast--' + variant : '');
    
    if (actionCallback) {
      var actBtn = toast.querySelector('[data-plush-toast-action]');
      if (actBtn) {
        actBtn.onclick = function (e) {
          e.preventDefault();
          toast.classList.remove('is-active');
          actionCallback();
        };
      }
    }
    
    clearTimeout(toastTimer);
    requestAnimationFrame(function () {
      toast.classList.add('is-active');
    });
    toastTimer = setTimeout(function () {
      toast.classList.remove('is-active');
    }, 4500);
  }

  var scrollLockCount = 0;
  function lockScroll() {
    scrollLockCount++;
    document.documentElement.style.overflow = 'hidden';
  }
  function unlockScroll() {
    scrollLockCount = Math.max(0, scrollLockCount - 1);
    if (scrollLockCount === 0) document.documentElement.style.overflow = '';
  }

  var FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
  function trapFocus(container) {
    var focusables = container.querySelectorAll(FOCUSABLE);
    if (!focusables.length) return function () {};
    var first = focusables[0];
    var last = focusables[focusables.length - 1];
    function handler(e) {
      if (e.key !== 'Tab') return;
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault(); last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault(); first.focus();
      }
    }
    container.addEventListener('keydown', handler);
    first.focus();
    return function () { container.removeEventListener('keydown', handler); };
  }

  function cartAddItems(items) {
    return fetch((ROUTES.cartAdd || '/cart/add') + '.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: items })
    }).then(function (r) { return r.json(); }).then(function (data) {
      if (data.status) { throw new Error(data.description || STRINGS.cartError); }
      return data;
    });
  }

  function fetchCart() {
    return fetch('/cart.js', { headers: { Accept: 'application/json' } }).then(function (r) { return r.json(); });
  }

  function changeLine(line, quantity) {
    return fetch((ROUTES.cartChange || '/cart/change') + '.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ line: line, quantity: quantity })
    }).then(function (r) { return r.json(); });
  }

  /* ================= Cart drawer ================= */

  function initCartDrawer() {
    var overlay = document.querySelector('[data-cart-drawer-overlay]');
    var drawer = document.querySelector('[data-cart-drawer]');
    if (!drawer || !overlay) return;
    var releaseTrap = function () {};
    var lastFocused = null;
    var hideTimer = null;

    function open() {
      clearTimeout(hideTimer);
      lastFocused = document.activeElement;
      overlay.hidden = false;
      drawer.hidden = false;
      requestAnimationFrame(function () {
        overlay.classList.add('is-visible');
        drawer.classList.add('is-open');
      });
      lockScroll();
      releaseTrap = trapFocus(drawer);
      document.addEventListener('keydown', onKeydown);
    }

    function close() {
      overlay.classList.remove('is-visible');
      drawer.classList.remove('is-open');
      unlockScroll();
      releaseTrap();
      document.removeEventListener('keydown', onKeydown);
      clearTimeout(hideTimer);
      hideTimer = setTimeout(function () { overlay.hidden = true; drawer.hidden = true; }, 350);
      if (lastFocused) lastFocused.focus();
    }

    function onKeydown(e) { if (e.key === 'Escape') close(); }

    overlay.addEventListener('click', function () {
      if (!document.querySelector('[data-quick-view]:not([hidden])') && !document.querySelector('[data-search-overlay].is-visible')) close();
    });
    document.querySelectorAll('[data-cart-drawer-close]').forEach(function (btn) {
      btn.addEventListener('click', close);
    });

    document.addEventListener('click', function (e) {
      var trigger = e.target.closest('[data-cart-open]');
      if (!trigger) return;
      if (SETTINGS.cartType === 'page') return; // let the link navigate normally
      e.preventDefault();
      refreshDrawer().then(open);
    });

    function render(cart) {
      if (cart.currency) CURRENT_CURRENCY = cart.currency;
      var itemsEl = drawer.querySelector('[data-cart-drawer-items]');
      var emptyEl = drawer.querySelector('[data-cart-drawer-empty]');
      var footerEl = drawer.querySelector('[data-cart-drawer-footer]');
      var giftEl = drawer.querySelector('[data-cart-drawer-gift]');
      var upsellEl = drawer.querySelector('[data-cart-drawer-upsell]');
      var countEls = document.querySelectorAll('[data-cart-count]');
      var titleCountEl = drawer.querySelector('[data-cart-drawer-count]');
      var subtotalEl = drawer.querySelector('[data-cart-drawer-subtotal]');
      var subtotalBtnEl = drawer.querySelector('[data-cart-drawer-subtotal-btn]');

      countEls.forEach(function (el) { el.textContent = cart.item_count; el.hidden = cart.item_count === 0; });
      if (titleCountEl) titleCountEl.textContent = '(' + cart.item_count + ')';
      if (subtotalEl) subtotalEl.textContent = formatMoney(cart.total_price);
      if (subtotalBtnEl) subtotalBtnEl.textContent = formatMoney(cart.total_price);
      if (footerEl) footerEl.hidden = cart.item_count === 0;
      if (giftEl) giftEl.hidden = cart.item_count === 0;
      if (upsellEl) upsellEl.hidden = cart.item_count === 0;
      if (emptyEl) emptyEl.hidden = cart.item_count > 0;

      if (itemsEl) {
        if (cart.item_count === 0) {
          itemsEl.innerHTML = '';
        } else {
          itemsEl.innerHTML = cart.items.map(function (item, i) {
            var line = i + 1;
            var safeUrl = escapeHtml(item.url);
            var variant = item.variant_title ? '<p class="cart-drawer__item-variant">' + escapeHtml(item.variant_title) + '</p>' : '';
            var rawImg = item.image ? item.image.replace(/(\.[a-z]+)(\?|$)/i, '_240x$1$2') : '';
            if (rawImg && rawImg.indexOf('//') === 0) rawImg = 'https:' + rawImg;
            var img = rawImg ? '<img src="' + escapeHtml(rawImg) + '" alt="' + escapeHtml(item.title) + '" loading="lazy" width="240" height="320">' : '';

            return (
              '<div class="cart-drawer__item" data-cart-drawer-line="' + line + '" data-variant-id="' + item.variant_id + '">' +
                '<a href="' + safeUrl + '" class="cart-drawer__item-media">' + img + '</a>' +
                '<div class="cart-drawer__item-info">' +
                  '<div class="cart-drawer__item-top">' +
                    '<a class="cart-drawer__item-title" href="' + safeUrl + '">' + escapeHtml(item.product_title) + '</a>' +
                    '<span class="cart-drawer__item-price">' + formatMoney(item.final_line_price) + '</span>' +
                  '</div>' +
                  variant +
                  '<div class="cart-drawer__item-actions">' +
                    '<div class="quantity-selector quantity-selector--luxury">' +
                      '<button type="button" data-cart-qty="down" data-line="' + line + '" aria-label="Decrease quantity">&minus;</button>' +
                      '<span data-cart-qty-value>' + item.quantity + '</span>' +
                      '<button type="button" data-cart-qty="up" data-line="' + line + '" aria-label="Increase quantity">+</button>' +
                    '</div>' +
                    '<button type="button" class="cart-drawer__item-remove" data-cart-remove data-line="' + line + '">' +
                      'Remove' +
                    '</button>' +
                  '</div>' +
                '</div>' +
              '</div>'
            );
          }).join('');
        }
      }

      var bar = drawer.querySelector('[data-shipping-bar]');
      var threshold = SETTINGS.freeShippingThreshold > 0 ? SETTINGS.freeShippingThreshold : 5000;
      if (bar && threshold > 0) {
        var remaining = Math.max(0, threshold - cart.total_price);
        var percent = Math.min(100, Math.round((cart.total_price / threshold) * 100));
        var textEl = bar.querySelector('[data-shipping-bar-text]');
        var fillEl = bar.querySelector('[data-shipping-bar-fill]');
        if (textEl) {
          textEl.textContent = remaining === 0
            ? "Complimentary Shipping Unlocked"
            : "Add " + formatMoney(remaining) + " for Complimentary Shipping";
        }
        if (fillEl) fillEl.style.width = percent + '%';
      }

      var upsellCards = drawer.querySelectorAll('[data-cart-drawer-upsell-list] .cart-drawer__upsell-card');
      if (upsellCards.length) {
        var cartProductIds = cart.items.map(function (i) { return String(i.product_id); });
        upsellCards.forEach(function (card) {
          var inCart = cartProductIds.indexOf(String(card.dataset.productId)) !== -1;
          card.style.display = inCart ? 'none' : '';
        });
      }
    }

    function refreshDrawer() {
      return fetchCart().then(render);
    }

    // Gift note sync
    var giftNoteInput = drawer.querySelector('[data-cart-gift-note]');
    var giftNoteTimer = null;
    if (giftNoteInput) {
      giftNoteInput.addEventListener('input', function () {
        clearTimeout(giftNoteTimer);
        giftNoteTimer = setTimeout(function () {
          fetch('/cart/update.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ note: giftNoteInput.value })
          });
        }, 500);
      });
    }

    drawer.addEventListener('click', function (e) {
      var qtyBtn = e.target.closest('[data-cart-qty]');
      var removeBtn = e.target.closest('[data-cart-remove]');
      if (qtyBtn) {
        var line = parseInt(qtyBtn.dataset.line, 10);
        var selector = qtyBtn.closest('.quantity-selector');
        if (selector) selector.setAttribute('aria-busy', 'true');
        qtyBtn.setAttribute('aria-busy', 'true');
        var valueEl = qtyBtn.parentElement.querySelector('[data-cart-qty-value]');
        var current = parseInt(valueEl.textContent, 10) || 1;
        var next = qtyBtn.dataset.cartQty === 'up' ? current + 1 : Math.max(0, current - 1);
        changeLine(line, next)
          .then(render)
          .catch(function (err) { showToast(err.message || STRINGS.cartError); })
          .finally(function () {
            if (selector) selector.removeAttribute('aria-busy');
            qtyBtn.removeAttribute('aria-busy');
          });
      }
      if (removeBtn) {
        var line = parseInt(removeBtn.dataset.line, 10);
        removeBtn.setAttribute('aria-busy', 'true');
        changeLine(line, 0)
          .then(render)
          .catch(function (err) { showToast(err.message || STRINGS.cartError); })
          .finally(function () {
            removeBtn.removeAttribute('aria-busy');
          });
      }
    });

    // Checkout form loading state
    drawer.addEventListener('submit', function (e) {
      var checkoutForm = e.target.closest('.cart-drawer__checkout-form');
      if (checkoutForm) {
        var btn = checkoutForm.querySelector('.cart-drawer__checkout-btn');
        if (btn) btn.setAttribute('aria-busy', 'true');
      }
    });

    // Add-to-cart: product forms + quick-add buttons everywhere on the site
    document.addEventListener('submit', function (e) {
      var form = e.target.closest('form[data-product-form]');
      if (!form) return;
      e.preventDefault();
      var idInput = form.querySelector('[name="id"]');
      var qtyInput = form.querySelector('[name="quantity"]');
      var submit = form.querySelector('[type="submit"]');
      if (!idInput || !idInput.value) return;
      if (submit) submit.setAttribute('aria-busy', 'true');

      var qty = qtyInput ? parseInt(qtyInput.value, 10) || 1 : 1;
      var mainItem = { id: parseInt(idInput.value, 10), quantity: qty };

      var properties = {};
      var hasProperties = false;
      Array.prototype.forEach.call(form.querySelectorAll('[name^="properties["]'), function (field) {
        if (field.disabled || !field.value) return;
        var match = field.name.match(/^properties\[(.+)\]$/);
        if (!match) return;
        properties[match[1]] = field.value;
        hasProperties = true;
      });
      if (hasProperties) mainItem.properties = properties;

      var items = [mainItem];
      var monogramToggle = form.querySelector('[data-monogram-toggle]');
      if (monogramToggle && monogramToggle.checked && monogramToggle.dataset.addonVariantId) {
        items.push({ id: parseInt(monogramToggle.dataset.addonVariantId, 10), quantity: qty });
      }

      cartAddItems(items)
        .then(function () { return refreshDrawer(); })
        .then(function () {
          if (SETTINGS.cartType === 'page') { window.location.href = ROUTES.cart || '/cart'; return; }
          open();
        })
        .catch(function (err) { showToast(err.message || STRINGS.cartError); })
        .finally(function () { if (submit) submit.removeAttribute('aria-busy'); });
    });

    document.addEventListener('click', function (e) {
      var quickAddBtn = e.target.closest('[data-quick-add]');
      if (!quickAddBtn) return;
      e.preventDefault();
      quickAddBtn.setAttribute('aria-busy', 'true');
      cartAddItems([{ id: parseInt(quickAddBtn.dataset.quickAdd, 10), quantity: 1 }])
        .then(function () { return refreshDrawer(); })
        .then(function () {
          if (SETTINGS.cartType === 'page') { window.location.href = ROUTES.cart || '/cart'; return; }
          open();
        })
        .catch(function (err) { showToast(err.message || STRINGS.cartError); })
        .finally(function () { quickAddBtn.removeAttribute('aria-busy'); });
    });

    // Initial paint from the live cart so drawer state is accurate even after
    // browser back/forward or a cart mutation elsewhere in the same tab.
    refreshDrawer();

    window.theme.cart = { refresh: refreshDrawer, open: open, close: close, addItems: cartAddItems, formatMoney: formatMoney };
  }

  /* ================= Predictive search ================= */

  function initSearchOverlay() {
    var overlay = document.querySelector('[data-search-overlay]');
    if (!overlay) return;
    var input = overlay.querySelector('[data-search-input]');
    var results = overlay.querySelector('[data-search-results]');
    var suggested = overlay.querySelector('[data-search-suggested]');
    var dynamic = overlay.querySelector('[data-search-dynamic]');
    var recent = overlay.querySelector('[data-search-recent]');
    var recentChips = overlay.querySelector('[data-search-recent-chips]');
    var recentClearBtn = overlay.querySelector('[data-search-recent-clear]');
    var debounceTimer = null;
    var lastFocused = null;
    var releaseTrap = function () {};
    var hideTimer = null;

    /* "Smart search" = the overlay remembers what you actually looked for
       and resurfaces it — the same pattern SSENSE/Net-a-Porter use so a
       returning shopper never has to retype. Stored client-side only. */
    var RECENT_KEY = 'plushington:recentSearches';
    function getRecent() {
      try { return JSON.parse(localStorage.getItem(RECENT_KEY)) || []; } catch (e) { return []; }
    }
    function saveRecent(term) {
      try {
        var list = getRecent().filter(function (t) { return t.toLowerCase() !== term.toLowerCase(); });
        list.unshift(term);
        localStorage.setItem(RECENT_KEY, JSON.stringify(list.slice(0, 6)));
      } catch (e) {}
    }
    function renderRecent() {
      var list = getRecent();
      if (!list.length) { recent.hidden = true; return; }
      recent.hidden = false;
      recentChips.innerHTML = list.map(function (t) {
        return '<button type="button" class="search-overlay__chip" data-recent-term="' + escapeHtml(t) + '">' + escapeHtml(t) + '</button>';
      }).join('');
    }
    if (recentChips) {
      recentChips.addEventListener('click', function (e) {
        var btn = e.target.closest('[data-recent-term]');
        if (!btn) return;
        input.value = btn.dataset.recentTerm;
        input.focus();
        runSearch(btn.dataset.recentTerm);
      });
    }
    if (recentClearBtn) {
      recentClearBtn.addEventListener('click', function () {
        try { localStorage.removeItem(RECENT_KEY); } catch (e) {}
        renderRecent();
      });
    }

    /* Toggling visibility instead of replacing results.innerHTML keeps the
       static "Shop By Category" imagery and recent-search chips alive —
       previously every keystroke nuked and rebuilt the entire results
       subtree, which is what silently wiped out real markup edits. */
    function showDefaultState() {
      dynamic.hidden = true;
      dynamic.innerHTML = '';
      suggested.hidden = false;
      renderRecent();
    }
    function showDynamicState() {
      suggested.hidden = true;
      recent.hidden = true;
      dynamic.hidden = false;
    }

    /* The on-screen keyboard resizes the browser's *visual* viewport, not
       its layout viewport — so CSS dvh (which only tracks browser-chrome
       show/hide) stays put while the keyboard covers half the screen.
       visualViewport is the one API both iOS Safari and Android Chrome
       fire reliably when the keyboard opens/closes, so it drives the
       panel's real max-height via --kb-vh (see base.css). */
    function syncViewportHeight() {
      if (!window.visualViewport) return;
      overlay.style.setProperty('--kb-vh', window.visualViewport.height + 'px');
    }
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', syncViewportHeight);
      window.visualViewport.addEventListener('scroll', syncViewportHeight);
    }

    /* Scrolling the results is the natural, expected gesture for "get the
       keyboard out of the way" on mobile — mirrors how Google/Amazon/most
       native apps behave, so nobody has to hunt for a dismiss control. */
    results.addEventListener('touchmove', function () {
      if (document.activeElement === input) input.blur();
    }, { passive: true });

    function open() {
      clearTimeout(hideTimer);
      lastFocused = document.activeElement;
      overlay.hidden = false;
      syncViewportHeight();
      if (input.value.trim().length < 2) showDefaultState();
      requestAnimationFrame(function () { overlay.classList.add('is-visible'); });
      lockScroll();
      releaseTrap = trapFocus(overlay.querySelector('.search-overlay__panel'));
      setTimeout(function () { input.focus(); }, 80);
      document.addEventListener('keydown', onKeydown);
    }
    function close() {
      overlay.classList.remove('is-visible');
      unlockScroll();
      releaseTrap();
      document.removeEventListener('keydown', onKeydown);
      clearTimeout(hideTimer);
      hideTimer = setTimeout(function () { overlay.hidden = true; }, 250);
      if (lastFocused) lastFocused.focus();
    }
    function onKeydown(e) { if (e.key === 'Escape') close(); }

    document.querySelectorAll('[data-search-open]').forEach(function (link) {
      link.addEventListener('click', function (e) { e.preventDefault(); open(); });
    });
    overlay.querySelectorAll('[data-search-close]').forEach(function (btn) { btn.addEventListener('click', close); });

    renderRecent();

    input.addEventListener('input', function () {
      var term = input.value.trim();
      clearTimeout(debounceTimer);
      if (term.length < 2) {
        showDefaultState();
        return;
      }
      debounceTimer = setTimeout(function () { runSearch(term); }, 180);
    });

    /* Arrow-key navigation across result cards, mirroring the lightbox's
       keyboard-first treatment elsewhere in the theme. */
    input.addEventListener('keydown', function (e) {
      if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
      var cards = dynamic.querySelectorAll('.search-overlay__card');
      if (!cards.length) return;
      e.preventDefault();
      (e.key === 'ArrowDown' ? cards[0] : cards[cards.length - 1]).focus();
    });
    results.addEventListener('keydown', function (e) {
      if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
      var cards = Array.prototype.slice.call(dynamic.querySelectorAll('.search-overlay__card'));
      var i = cards.indexOf(document.activeElement);
      if (i === -1) return;
      e.preventDefault();
      var next = e.key === 'ArrowDown' ? cards[i + 1] : cards[i - 1];
      if (next) next.focus(); else input.focus();
    });

    /* Bolds the matched substring in a result title — the small "the
       system understood what I typed" signal Google/Amazon give, cheap
       to add since we already have the raw term and title in hand. */
    function highlightMatch(text, term) {
      var idx = term ? text.toLowerCase().indexOf(term.toLowerCase()) : -1;
      if (idx === -1) return escapeHtml(text);
      return escapeHtml(text.slice(0, idx)) +
        '<mark class="search-overlay__match">' + escapeHtml(text.slice(idx, idx + term.length)) + '</mark>' +
        escapeHtml(text.slice(idx + term.length));
    }

    function runSearch(term) {
      showDynamicState();
      dynamic.innerHTML = '<div class="search-overlay__loading"><span class="spinner" aria-hidden="true"></span></div>';

      var url = (ROUTES.predictiveSearch || '/search/suggest') +
        '.json?q=' + encodeURIComponent(term) +
        '&resources[type]=product&resources[limit]=6&resources[options][fields]=title,product_type,variants.sku,vendor';

      fetch(url, { headers: { Accept: 'application/json' } })
        .then(function (r) { return r.json(); })
        .then(function (data) {
          var products = (data.resources && data.resources.results && data.resources.results.products) || [];
          if (!products.length) {
            dynamic.innerHTML =
              '<p class="search-overlay__empty">No results for &ldquo;' + escapeHtml(term) + '&rdquo;</p>' +
              '<div class="search-overlay__no-results">' +
                '<span class="search-overlay__suggested-label">Try Instead</span>' +
                '<div class="search-overlay__chips">' +
                  '<a class="search-overlay__chip" href="' + ROUTES.root + 'collections/new-arrivals">New Arrivals</a>' +
                  '<a class="search-overlay__chip" href="' + ROUTES.root + 'collections/scarves">Scarves</a>' +
                  '<a class="search-overlay__chip" href="' + ROUTES.root + 'collections/shawls">Shawls &amp; Wraps</a>' +
                  '<a class="search-overlay__chip" href="' + ROUTES.root + 'collections/all">Full Collection</a>' +
                '</div>' +
              '</div>';
            return;
          }

          saveRecent(term);

          dynamic.innerHTML =
            '<p class="search-overlay__count">' + products.length + (products.length === 1 ? ' result' : ' results') + ' for &ldquo;' + escapeHtml(term) + '&rdquo;</p>' +
            '<div class="search-overlay__grid">' +
              products.map(function (p, i) {
                var rawImg = p.featured_image ? p.featured_image.url.replace(/(\.[a-z]+)(\?|$)/i, '_400x$1$2') : (p.image || '');
                if (rawImg && rawImg.indexOf('//') === 0) rawImg = 'https:' + rawImg;
                var img = escapeHtml(rawImg);
                var onSale = p.compare_at_price_min && parseFloat(p.compare_at_price_min) > parseFloat(p.price_max || p.price);
                var priceHTML = onSale
                  ? '<span class="search-overlay__card-price is-sale">' +
                      '<span class="search-overlay__card-price-now">' + formatMoney(p.price) + '</span>' +
                      '<span class="search-overlay__card-price-was">' + formatMoney(p.compare_at_price_min) + '</span>' +
                    '</span>'
                  : '<span class="search-overlay__card-price">' + formatMoney(p.price) + '</span>';
                return (
                  '<a class="search-overlay__card" href="' + escapeHtml(p.url) + '" style="animation-delay:' + (i * 40) + 'ms">' +
                    '<div class="search-overlay__card-media">' +
                      (img ? '<img src="' + img + '" alt="' + escapeHtml(p.title) + '" loading="lazy" width="200" height="200">' : '') +
                      (onSale ? '<span class="search-overlay__card-badge">Sale</span>' : '') +
                    '</div>' +
                    '<div class="search-overlay__card-info">' +
                      (p.type ? '<span class="search-overlay__card-type">' + escapeHtml(p.type) + '</span>' : '') +
                      '<h4 class="search-overlay__card-title">' + highlightMatch(p.title, term) + '</h4>' +
                      priceHTML +
                    '</div>' +
                  '</a>'
                );
              }).join('') +
            '</div>' +
            '<a class="search-overlay__view-all-link" href="' + (ROUTES.search || '/search') + '?q=' + encodeURIComponent(term) + '">' +
              'View all results (' + products.length + ') &rarr;' +
            '</a>';
        })
        .catch(function () {
          dynamic.innerHTML = '<p class="search-overlay__empty">Unable to complete search.</p>';
        });
    }
  }

  /* ================= Quick view ================= */

  function initQuickView() {
    var modal = document.querySelector('[data-quick-view]');
    if (!modal) return;
    var overlay = modal.querySelector('[data-quick-view-overlay]');
    var content = modal.querySelector('[data-quick-view-content]');
    var lastFocused = null;
    var releaseTrap = function () {};
    var hideTimer = null;

    function open() {
      clearTimeout(hideTimer);
      lastFocused = document.activeElement;
      modal.hidden = false;
      if (overlay) {
        overlay.hidden = false;
        overlay.classList.add('is-visible');
      }
      requestAnimationFrame(function () { modal.classList.add('is-visible'); });
      lockScroll();
      document.addEventListener('keydown', onKeydown);
    }
    function close() {
      modal.classList.remove('is-visible');
      if (overlay) overlay.classList.remove('is-visible');
      unlockScroll();
      releaseTrap();
      document.removeEventListener('keydown', onKeydown);
      clearTimeout(hideTimer);
      hideTimer = setTimeout(function () {
        modal.hidden = true;
        if (overlay) overlay.hidden = true;
        content.innerHTML = '<div class="quick-view__loading" data-quick-view-loading><span class="spinner" aria-hidden="true"></span></div>';
      }, 250);
      if (lastFocused) lastFocused.focus();
    }
    function onKeydown(e) { if (e.key === 'Escape') close(); }

    if (overlay) overlay.addEventListener('click', close);
    modal.addEventListener('click', function (e) { if (e.target.closest('[data-quick-view-close]')) close(); });

    document.addEventListener('click', function (e) {
      var trigger = e.target.closest('[data-quick-view-open]');
      if (!trigger) return;
      e.preventDefault();
      e.stopPropagation();
      var handle = trigger.dataset.productHandle;
      var rawUrl = trigger.dataset.productUrl || '';
      var cleanHandle = handle || rawUrl.split('?')[0].split('/').filter(Boolean).pop();
      if (!cleanHandle) return;
      var fetchUrl = (ROUTES.root || '/').replace(/\/$/, '') + '/products/' + cleanHandle + '.js';
      
      open();
      fetch(fetchUrl, { headers: { Accept: 'application/json' } })
        .then(function (r) {
          if (!r.ok) throw new Error('Product not found');
          return r.json();
        })
        .then(renderProduct)
        .catch(function () {
          content.innerHTML = '<div style="padding:3rem 2rem; text-align:center;"><p class="quick-view__info" style="margin-bottom:1.5rem; color:#8A6F4D; font-family:var(--font-heading); font-size:1.2rem;">Plushington Atelier Piece</p><p style="color:var(--color-text-light); font-size:0.9rem;">Unable to load preview at this moment.</p><a href="' + escapeHtml(rawUrl || '#') + '" class="button button--primary" style="margin-top:1rem; display:inline-block;">View Product Page &rarr;</a></div>';
        });
    });

    var SWATCH_COLORS = {
      camel: '#C19A6B', oatmeal: '#E3DAC9', charcoal: '#36454F', ecru: '#FDFBF7',
      ivory: '#F5F5F0', black: '#141210', grey: '#8A8A86', gray: '#8A8A86',
      navy: '#000080', 'navy-blue': '#000080', denim: '#2C4A6E', 'denim-blue': '#2C4A6E',
      forest: '#2D3830', spruce: '#2D3830', sage: '#A8B89C', 'sage-green': '#A8B89C',
      burgundy: '#800020', merlot: '#5A2A27', petrol: '#0F4C5C',
      blush: '#E8C4C4', rust: '#9C3F2F', terracotta: '#C46B4D', taupe: '#8B7D6B',
      stone: '#B8AD9E', champagne: '#F0E2C6', gold: '#C9A84C', silver: '#C0C0C0',
      khaki: '#B4A369', beige: '#E8DCC8', coffee: '#4B3621', yellow: '#F0D030',
      green: '#5C7A52', blue: '#3A5A8C', 'army-green': '#4B5320', apricot: '#FBCEB1',
      mint: '#AAE8C4', 'peacock-blue': '#1B6F8C', toffee: '#8C6A45', onyx: '#26262A',
      crimson: '#9C2B3A', amber: '#C66A2E', coral: '#C97B6D', mustard: '#C9A227',
      'powder-blue': '#A9BFC9', 'dusty-blue': '#7F92A0', violet: '#6F5A82',
      'teal-green': '#2D5F56', 'light-grey': '#B7B3AC', 'light-gray': '#B7B3AC',
      'slate-grey': '#64645F', 'slate-gray': '#64645F', emerald: '#1F7A5C',
      'kelly-green': '#3F8A4A', 'rose-pink': '#C85777', magenta: '#9C2F66',
      'almond-green': '#8A9B7A'
    };
    function swatchColor(name) {
      var key = String(name).toLowerCase().trim().replace(/\s+/g, '-');
      return SWATCH_COLORS[key] || '#C9C4BB';
    }

    function renderProduct(product) {
      var variant = product.variants && product.variants.length ? product.variants[0] : { id: product.id, price: product.price, available: product.available };
      var rawImages = product.images || [];
      var mediaSrc = rawImages.length ? rawImages[0] : (product.featured_image || '');
      if (mediaSrc && mediaSrc.indexOf('//') === 0) mediaSrc = 'https:' + mediaSrc;
      
      var optionsHtml = '';
      var rawOptions = product.options || [];
      var hasRealOptions = false;
      
      if (product.variants && product.variants.length > 1) {
        hasRealOptions = true;
      } else if (rawOptions.length > 0) {
        var firstOptName = typeof rawOptions[0] === 'object' ? (rawOptions[0].name || '') : String(rawOptions[0] || '');
        if (firstOptName && firstOptName.toLowerCase() !== 'title' && firstOptName.toLowerCase() !== 'default title') {
          hasRealOptions = true;
        }
      }
      
      if (hasRealOptions) {
        optionsHtml = rawOptions.map(function (optObj, idx) {
          var optionName = typeof optObj === 'object' ? (optObj.name || '') : String(optObj || '');
          if (!optionName || optionName.toLowerCase() === 'title') return '';
          var values = (typeof optObj === 'object' && optObj.values) ? optObj.values : Array.from(new Set(product.variants.map(function (v) { return v['option' + (idx + 1)]; }))).filter(Boolean);
          if (!values.length) return '';
          var isColor = /^colou?r$/i.test(optionName.trim());
          return (
            '<fieldset class="variant-fieldset" style="margin-bottom:1.2rem; border:none; padding:0;">' +
              '<legend style="font-size:0.75rem; letter-spacing:0.18em; text-transform:uppercase; font-weight:500; margin-bottom:0.6rem; color:#141210;">' +
                escapeHtml(optionName) + (isColor ? ': <span style="font-weight:400; color:#8A6F4D;" data-qv-option-label-' + idx + '>' + escapeHtml(values[0]) + '</span>' : '') +
              '</legend>' +
              '<div class="variant-options' + (isColor ? ' variant-options--swatch' : '') + '" style="display:flex; flex-wrap:wrap; gap:0.5rem;">' +
                values.map(function (val, vi) {
                  var id = 'qv-opt-' + idx + '-' + vi;
                  var input = '<input type="radio" id="' + id + '" name="qv-option-' + idx + '" value="' + escapeHtml(val) + '" data-qv-option' + (isColor ? ' data-qv-option-idx="' + idx + '"' : '') + (vi === 0 ? ' checked' : '') + ' style="position:absolute; opacity:0; pointer-events:none;">';
                  var label = isColor
                    ? '<label for="' + id + '" class="swatch-label' + (vi === 0 ? ' is-active' : '') + '" title="' + escapeHtml(val) + '" style="cursor:pointer; display:inline-flex; align-items:center; justify-content:center; width:36px; height:36px; border-radius:50%; background:' + escapeHtml(swatchColor(val)) + '; box-shadow:inset 0 0 0 1px rgba(20,18,16,0.1), 0 1px 2px rgba(20,18,16,0.08); transition:box-shadow 0.2s ease;"><span class="visually-hidden">' + escapeHtml(val) + '</span></label>'
                    : '<label for="' + id + '" style="cursor:pointer; display:inline-block; padding:0.4rem 0.9rem; border:1px solid rgba(20,18,16,0.2); font-size:0.74rem; letter-spacing:0.1em; text-transform:uppercase; transition:all 0.2s ease; background:#fff;">' + escapeHtml(val) + '</label>';
                  return '<div class="qv-opt-wrapper">' + input + label + '</div>';
                }).join('') +
              '</div>' +
            '</fieldset>'
          );
        }).join('');
      }

      content.innerHTML =
        '<div class="quick-view__grid" style="display:grid; grid-template-columns:1fr 1.15fr; background:#fff; overflow:hidden;">' +
          '<div class="quick-view__media" style="aspect-ratio:1/1; overflow:hidden; background:#F7F4EF; position:relative;">' +
            (mediaSrc ? '<img data-qv-main-img src="' + escapeHtml(mediaSrc) + '" alt="' + escapeHtml(product.title) + '" style="width:100%; height:100%; object-fit:cover; display:block;" width="800" height="1067">' : '<div style="width:100%; height:100%; background:#F7F4EF;"></div>') +
          '</div>' +
          '<div class="quick-view__info" style="padding:clamp(1.8rem, 3.5vw, 3rem); display:flex; flex-direction:column; justify-content:center;">' +
            '<p style="font-size:0.68rem; letter-spacing:0.26em; text-transform:uppercase; color:#8A6F4D; font-weight:500; margin:0 0 0.5rem;">Plushington Atelier &bull; FW26</p>' +
            '<h2 class="product-info__title" style="font-family:var(--font-heading); font-size:clamp(1.3rem, 2.2vw, 1.7rem); font-weight:400; letter-spacing:0.03em; color:#141210; margin:0 0 0.75rem; line-height:1.3;">' + escapeHtml(product.title) + '</h2>' +
            '<div class="price" style="margin-bottom:1.5rem; font-size:1.1rem; letter-spacing:0.04em; color:#141210;"><span class="price__current" data-qv-price>' + formatMoney(variant.price) + '</span></div>' +
            optionsHtml +
            '<div style="background:#FDFBF7; border:1px solid rgba(212,190,150,0.35); padding:0.85rem 1rem; margin-bottom:1.5rem; display:flex; align-items:center; gap:0.75rem;">' +
              '<span style="color:#8A6F4D; font-size:1.1rem;">✦</span>' +
              '<span style="font-size:0.72rem; letter-spacing:0.08em; text-transform:uppercase; color:#141210; font-weight:500;">Includes Bespoke Keepsake Box & Silk Ribbon</span>' +
            '</div>' +
            '<div class="product-form__row" style="margin-top:0.5rem;">' +
              '<button type="button" class="button button--primary product-form__submit" data-qv-add data-variant-id="' + Number(variant.id) + '"' + (variant.available ? '' : ' disabled') + ' style="width:100%; padding:1.05rem 1.8rem; background:#141210; color:#FDFBF7; font-size:0.76rem; letter-spacing:0.22em; text-transform:uppercase; font-weight:500; border:1px solid #141210; cursor:pointer; transition:all 0.3s ease;">' +
                (variant.available ? 'Add to Bag &bull; <span data-qv-btn-price>' + formatMoney(variant.price) + '</span>' : escapeHtml(STRINGS.soldOut || 'Sold Out')) +
              '</button>' +
            '</div>' +
            '<a class="link-underline quick-view__view-full" href="' + escapeHtml(product.url || ('/products/' + product.handle)) + '" style="display:block; margin-top:1.2rem; text-align:center; font-size:0.72rem; letter-spacing:0.18em; text-transform:uppercase; color:var(--color-text-light); text-decoration:none;">' + escapeHtml(STRINGS.viewFullDetails || 'View Complete Product Details') + ' &rarr;</a>' +
          '</div>' +
        '</div>';

      releaseTrap = trapFocus(content);

      var variants = product.variants;
      content.querySelectorAll('[data-qv-option]').forEach(function (input) {
        input.addEventListener('change', function () {
          if (input.hasAttribute('data-qv-option-idx')) {
            var labelEl = content.querySelector('[data-qv-option-label-' + input.dataset.qvOptionIdx + ']');
            if (labelEl) labelEl.textContent = input.value;
            var group = input.closest('.variant-options');
            if (group) {
              group.querySelectorAll('.swatch-label').forEach(function (l) { l.classList.remove('is-active'); });
              var ownLabel = content.querySelector('label[for="' + input.id + '"]');
              if (ownLabel) ownLabel.classList.add('is-active');
            }
          }
          var chosen = Array.from(content.querySelectorAll('[data-qv-option]:checked')).map(function (i) { return i.value; });
          var match = variants.find(function (v) {
            return chosen.every(function (val, idx) { return v['option' + (idx + 1)] === val; });
          });
          var addBtn = content.querySelector('[data-qv-add]');
          var priceEl = content.querySelector('[data-qv-price]');
          var btnPriceEl = content.querySelector('[data-qv-btn-price]');
          var mainImgEl = content.querySelector('[data-qv-main-img]');
          if (match) {
            addBtn.dataset.variantId = match.id;
            addBtn.disabled = !match.available;
            if (match.available) {
              addBtn.innerHTML = 'Add to Bag &bull; <span data-qv-btn-price>' + formatMoney(match.price) + '</span>';
            } else {
              addBtn.textContent = STRINGS.soldOut;
            }
            if (priceEl) priceEl.textContent = formatMoney(match.price);
            if (match.featured_image && match.featured_image.src && mainImgEl) {
              mainImgEl.src = match.featured_image.src;
            }
          }
        });
      });

      content.querySelector('[data-qv-add]').addEventListener('click', function () {
        var btn = this;
        btn.setAttribute('aria-busy', 'true');
        btn.textContent = 'Adding to Bag...';
        (window.theme.cart.addItems([{ id: parseInt(btn.dataset.variantId, 10), quantity: 1 }]))
          .then(function () { return window.theme.cart.refresh(); })
          .then(function () {
            close();
            window.theme.cart.open();
          })
          .catch(function (err) { showToast(err.message || STRINGS.cartError); })
          .finally(function () {
            btn.removeAttribute('aria-busy');
          });
      });
    }
  }

  /* ================= Wishlist ================= */

  function getWishlist() {
    try { return JSON.parse(localStorage.getItem(WISHLIST_KEY)) || []; } catch (e) { return []; }
  }
  function saveWishlist(list) {
    localStorage.setItem(WISHLIST_KEY, JSON.stringify(list));
  }

  function initWishlist() {
    var drawer = document.querySelector('[data-wishlist-drawer]');
    var overlay = document.querySelector('[data-wishlist-drawer-overlay]');
    if (!drawer || !overlay) return;
    var releaseTrap = function () {};
    var lastFocused = null;
    var hideTimer = null;

    function syncButtons() {
      var ids = getWishlist().map(function (i) { return String(i.id); });
      document.querySelectorAll('[data-wishlist-toggle]').forEach(function (btn) {
        var isSaved = ids.indexOf(String(btn.dataset.productId)) !== -1;
        btn.setAttribute('aria-pressed', isSaved ? 'true' : 'false');
      });
      var badge = document.querySelector('[data-wishlist-header-count]');
      if (badge) {
        badge.textContent = ids.length;
        badge.hidden = ids.length === 0;
      }
    }

    function renderDrawer() {
      var list = getWishlist();
      var itemsEl = drawer.querySelector('[data-wishlist-items]');
      var emptyEl = drawer.querySelector('[data-wishlist-empty]');
      var countEl = drawer.querySelector('[data-wishlist-count]');
      if (countEl) countEl.textContent = '(' + list.length + ')';
      if (emptyEl) emptyEl.hidden = list.length > 0;
      if (!itemsEl) return;
      itemsEl.innerHTML = list.map(function (item) {
        var safeUrl = escapeHtml(item.url);
        var safeImg = escapeHtml(item.image);
        var safeId = escapeHtml(item.id);
        return (
          '<div class="wishlist-item" data-wishlist-item="' + safeId + '">' +
            '<a href="' + safeUrl + '" class="wishlist-item__media">' + (item.image ? '<img src="' + safeImg + '" alt="" loading="lazy" width="76" height="101">' : '') + '</a>' +
            '<div><a class="wishlist-item__title" href="' + safeUrl + '">' + escapeHtml(item.title) + '</a><span class="wishlist-item__price">' + formatMoney(item.price) + '</span></div>' +
            '<button type="button" class="wishlist-item__remove" data-wishlist-remove="' + safeId + '" aria-label="Remove">' +
              '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.4" aria-hidden="true"><path d="m5 5 14 14M19 5 5 19"/></svg>' +
            '</button>' +
          '</div>'
        );
      }).join('');
    }

    function open() {
      clearTimeout(hideTimer);
      lastFocused = document.activeElement;
      overlay.hidden = false; drawer.hidden = false;
      requestAnimationFrame(function () { overlay.classList.add('is-visible'); drawer.classList.add('is-open'); });
      lockScroll();
      renderDrawer();
      releaseTrap = trapFocus(drawer);
      document.addEventListener('keydown', onKeydown);
    }
    function close() {
      overlay.classList.remove('is-visible'); drawer.classList.remove('is-open');
      unlockScroll(); releaseTrap();
      document.removeEventListener('keydown', onKeydown);
      clearTimeout(hideTimer);
      hideTimer = setTimeout(function () { overlay.hidden = true; drawer.hidden = true; }, 350);
      if (lastFocused) lastFocused.focus();
    }
    function onKeydown(e) { if (e.key === 'Escape') close(); }

    document.querySelectorAll('[data-wishlist-open]').forEach(function (btn) { btn.addEventListener('click', open); });
    document.querySelectorAll('[data-wishlist-drawer-close]').forEach(function (btn) { btn.addEventListener('click', close); });
    overlay.addEventListener('click', close);

    document.addEventListener('click', function (e) {
      var toggle = e.target.closest('[data-wishlist-toggle]');
      if (toggle) {
        e.preventDefault();
        e.stopPropagation();
        var id = toggle.dataset.productId;
        var list = getWishlist();
        var idx = list.findIndex(function (i) { return String(i.id) === String(id); });
        var title = toggle.dataset.productTitle || 'Product';
        if (idx === -1) {
          list.push({ id: id, title: title, url: toggle.dataset.productUrl, image: toggle.dataset.productImage, price: toggle.dataset.productPrice });
          showToast(title + ' saved to Wishlist', 'wishlist', open, 'View Wishlist');
        } else {
          list.splice(idx, 1);
          showToast(title + ' removed from Wishlist', 'info');
        }
        saveWishlist(list);
        syncButtons();
        return;
      }
      var removeBtn = e.target.closest('[data-wishlist-remove]');
      if (removeBtn) {
        var rid = removeBtn.dataset.wishlistRemove;
        saveWishlist(getWishlist().filter(function (i) { return String(i.id) !== String(rid); }));
        syncButtons();
        renderDrawer();
      }
    });

    syncButtons();
  }

  /* ================= Bundle widget ================= */

  function initBundleWidget() {
    var widget = document.querySelector('[data-bundle-widget]');
    if (!widget) return;
    var totalEl = widget.querySelector('[data-bundle-total]');
    var addBtn = widget.querySelector('[data-bundle-add]');

    function recalc() {
      var total = 0;
      widget.querySelectorAll('[data-bundle-item]').forEach(function (item) {
        var checkbox = item.querySelector('input[type="checkbox"]');
        if (checkbox.checked) total += parseInt(item.dataset.price, 10);
      });
      if (totalEl) totalEl.textContent = window.theme.cart.formatMoney(total);
    }

    widget.addEventListener('change', function (e) {
      if (e.target.matches('[data-bundle-check]')) recalc();
    });

    if (addBtn) {
      addBtn.addEventListener('click', function () {
        var items = [];
        widget.querySelectorAll('[data-bundle-item]').forEach(function (item) {
          var checkbox = item.querySelector('input[type="checkbox"]');
          if (checkbox.checked) items.push({ id: parseInt(item.dataset.variantId, 10), quantity: 1 });
        });
        if (!items.length) return;
        addBtn.setAttribute('aria-busy', 'true');
        window.theme.cart.addItems(items)
          .then(function () { return window.theme.cart.refresh(); })
          .then(function () { window.theme.cart.open(); })
          .catch(function (err) { showToast(err.message || STRINGS.cartError); })
          .finally(function () { addBtn.removeAttribute('aria-busy'); });
      });
    }

    recalc();
  }

  /* ================= Global Luxury Scroll Reveal Engine ================= */

  function initScrollReveal() {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    var elements = document.querySelectorAll(
      '.category-portal-card, .product-card, .atelier-pillar, .shop-the-look__media-wrap, .shop-the-look__card, .press-quote, .newsletter--luxury, .site-footer__col'
    );

    if (!('IntersectionObserver' in window)) {
      elements.forEach(function (el) { el.classList.add('is-revealed'); });
      return;
    }

    var observer = new IntersectionObserver(function (entries, obs) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-revealed');
          obs.unobserve(entry.target);
        }
      });
    }, {
      rootMargin: '0px 0px -40px 0px',
      threshold: 0.08
    });

    elements.forEach(function (el) {
      el.classList.add('reveal-item');
      observer.observe(el);
    });
  }

  /* ================= Welcome popup (first-visit email capture) ================= */
  function initWelcomePopup() {
    var popup = document.querySelector('[data-welcome-popup]');
    var overlay = document.querySelector('[data-welcome-overlay]');
    if (!popup || !overlay) return;

    var STORAGE_KEY = 'plush_welcome_state';
    var DELAY_MS = 5000;
    var justSucceeded = popup.hasAttribute('data-welcome-just-succeeded');
    var hasErrors = popup.hasAttribute('data-welcome-has-errors');
    var forceOpen = justSucceeded || hasErrors;

    var state = null;
    try { state = window.localStorage.getItem(STORAGE_KEY); } catch (e) {}
    if (state && !forceOpen) return;
    if (popup.hasAttribute('data-customer-logged-in') && !forceOpen) return;
    if (window.location.pathname.indexOf('/cart') === 0 && !forceOpen) return;
    // Never interrupt someone actively evaluating a product — a scroll-locking
    // popup mid-browse reads as the gallery/page breaking, and costs more in
    // lost conversions here than anywhere else on the site.
    if (document.body.classList.contains('template-product') && !forceOpen) return;

    var closeBtns = popup.querySelectorAll('[data-welcome-close]');
    var releaseTrap = function () {};
    var lastFocused = null;
    var openTimer = null;

    function onKeydown(e) { if (e.key === 'Escape') close(); }

    function open() {
      lastFocused = document.activeElement;
      overlay.hidden = false;
      popup.hidden = false;
      requestAnimationFrame(function () {
        overlay.classList.add('is-visible');
        popup.classList.add('is-visible');
      });
      lockScroll();
      releaseTrap = trapFocus(popup);
      document.addEventListener('keydown', onKeydown);
    }

    function close(remember) {
      overlay.classList.remove('is-visible');
      popup.classList.remove('is-visible');
      unlockScroll();
      releaseTrap();
      document.removeEventListener('keydown', onKeydown);
      setTimeout(function () { overlay.hidden = true; popup.hidden = true; }, 400);
      if (remember !== false) {
        try { window.localStorage.setItem(STORAGE_KEY, 'dismissed'); } catch (e) {}
      }
      if (lastFocused) lastFocused.focus();
    }

    closeBtns.forEach(function (btn) {
      btn.addEventListener('click', function () { close(); });
    });
    overlay.addEventListener('click', function () { close(); });

    var copyBtn = popup.querySelector('[data-welcome-copy]');
    if (copyBtn) {
      copyBtn.addEventListener('click', function () {
        var codeEl = popup.querySelector('[data-welcome-code]');
        var text = codeEl ? codeEl.textContent.trim() : '';
        navigator.clipboard.writeText(text).then(function () {
          copyBtn.textContent = 'Copied';
          copyBtn.classList.add('is-copied');
          setTimeout(function () {
            copyBtn.textContent = 'Copy';
            copyBtn.classList.remove('is-copied');
          }, 1600);
        });
      });
    }

    if (forceOpen) {
      if (justSucceeded) {
        try { window.localStorage.setItem(STORAGE_KEY, 'subscribed'); } catch (e) {}
      }
      open();
    } else {
      openTimer = setTimeout(open, DELAY_MS);
      window.addEventListener('beforeunload', function () { clearTimeout(openTimer); });
    }
  }

  /* ================= Ask AI ================= */

  function initAskAi() {
    var buttons = document.querySelectorAll('[data-ai-assistant]');
    if (!buttons.length) return;
    var hint = document.querySelector('[data-ask-ai-hint]');
    var hintTimer = null;

    buttons.forEach(function (btn) {
      btn.addEventListener('click', function () {
        var prompt = btn.dataset.aiPrompt || '';
        var urlTemplate = btn.dataset.aiUrl || '';
        var url = urlTemplate.indexOf('{query}') !== -1
          ? urlTemplate.replace('{query}', encodeURIComponent(prompt))
          : urlTemplate;

        // ChatGPT and Perplexity both accept ?q= as an auto-submitted
        // prompt (verified directly); Claude and Gemini require sign-in
        // before a composer exists to prefill, so the clipboard copy is
        // the reliable path there — harmless extra step for the other two.
        if (navigator.clipboard && navigator.clipboard.writeText) {
          navigator.clipboard.writeText(prompt).catch(function () {});
        }
        if (hint) {
          clearTimeout(hintTimer);
          hint.hidden = false;
          hintTimer = setTimeout(function () { hint.hidden = true; }, 4000);
        }
        window.open(url, '_blank', 'noopener');
      });
    });
  }
})();

